<template>
  <nav class="sticky top-0 z-50 border-b border-zinc-200 bg-base/80 backdrop-blur-lg transition-colors dark:border-white/5 dark:bg-base-dark/80">
    <div class="mx-auto flex h-16 max-w-6xl items-center justify-between px-4">
      <router-link
        to="/"
        class="rounded-lg outline-none ring-violet-500/40 transition hover:opacity-90 focus-visible:ring-2"
        aria-label="YouStory — Home"
      >
        <YouStoryLogo />
      </router-link>

      <div class="flex items-center gap-4 sm:gap-6">
        <template v-if="auth.isLoggedIn">
          <router-link
            to="/create"
            class="text-sm font-medium text-zinc-500 transition hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-50"
            active-class="!text-zinc-900 dark:!text-zinc-50"
          >
            Create
          </router-link>
          <router-link
            to="/history"
            class="text-sm font-medium text-zinc-500 transition hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-50"
            active-class="!text-zinc-900 dark:!text-zinc-50"
          >
            History
          </router-link>

          <div class="flex items-center gap-3">
            <img
              v-if="auth.user?.photoURL"
              :src="auth.user.photoURL"
              :alt="auth.user.displayName"
              class="h-8 w-8 rounded-full ring-2 ring-violet-500/30"
              referrerpolicy="no-referrer"
            />
            <div
              v-else
              class="flex h-8 w-8 items-center justify-center rounded-full bg-violet-600 text-xs font-bold text-white"
            >
              {{ auth.user?.displayName?.charAt(0) || '?' }}
            </div>
            <button
              @click="handleSignOut"
              class="rounded-lg border border-zinc-200 px-3 py-1.5 text-xs font-medium text-zinc-500 transition hover:border-zinc-300 hover:text-zinc-900 dark:border-white/10 dark:text-zinc-400 dark:hover:border-white/20 dark:hover:text-zinc-50"
            >
              Sign Out
            </button>
          </div>
        </template>

        <button
          v-else
          @click="handleSignIn"
          class="flex items-center gap-2 rounded-xl bg-zinc-900 px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-zinc-800 dark:bg-white dark:text-zinc-900 dark:shadow-white/5 dark:hover:bg-zinc-100"
        >
          <svg class="h-4 w-4" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
          </svg>
          Sign in with Google
        </button>

        <!-- Theme toggle -->
        <button
          @click="theme.toggle()"
          class="flex h-9 w-9 items-center justify-center rounded-xl border border-zinc-200 text-zinc-500 transition hover:border-zinc-300 hover:text-zinc-900 dark:border-white/10 dark:text-zinc-400 dark:hover:border-white/20 dark:hover:text-zinc-50"
          :aria-label="theme.isDark ? 'Switch to light mode' : 'Switch to dark mode'"
        >
          <!-- Sun (visible in dark mode) -->
          <svg v-if="theme.isDark" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z"/>
          </svg>
          <!-- Moon (visible in light mode) -->
          <svg v-else class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z"/>
          </svg>
        </button>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { useAuthStore } from '@/stores/auth';
import { useThemeStore } from '@/stores/theme';
import { useRouter } from 'vue-router';
import YouStoryLogo from '@/components/YouStoryLogo.vue';

const auth = useAuthStore();
const theme = useThemeStore();
const router = useRouter();

async function handleSignIn() {
  try {
    await auth.signIn();
    router.push('/create');
  } catch (e) {
    console.error('Sign-in failed:', e);
  }
}

async function handleSignOut() {
  await auth.signOut();
  router.push('/');
}
</script>
